import { Button } from '@1trading/react/components/ui/button';
import { GradientIcon } from '@1trading/react/components/ui/gradient-icon';
import { useIsMutating } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Banknote, CircleCheck, TrendingUp } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';

import { LinkCard, LinkCardContent, LinkCardDescription, LinkCardHeader, LinkCardTitle } from '@/components/ui/link-card';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { LdFeatureFlags } from '@/config/features';
import { OnboardingProgressStep, useProgress } from '@/context/progress';
import { useProfileVerificationInformation, useProfileVerificationSubmissionWait } from '@/hooks/queries/use-profile-verification-information';
import { QUERY_KEY as PUT_USER_QUERY_KEY, useUserPutMutation } from '@/hooks/queries/use-user';
import { useOnboardingCompleteRedirect } from '@/hooks/use-onboarding-complete-redirect';
import BaseLayout from '@/layouts/base';
import LoadingPage from '@/pages/loading';
import { AccountType, getAccountType } from '@/utils/account-type';

const ID = 'onboarding-complete-page';

const REDIRECT_DELAY_MS = 5_000;

const OnboardingCompletePageBasicContent = () => {
  const { t } = useTranslation();

  useEffect(() => {
    const timeout = setTimeout(() => {
      window.location.assign(import.meta.env.VITE_APP_EXCHANGE_URL);
    }, REDIRECT_DELAY_MS);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <BaseLayout className="-md:justify-center space-y-8" id={ID} size="sm">
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <CircleCheck />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>{t('onboardingProgress.complete.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('onboardingProgress.complete.description')}</PageHeadingDescription>
        <PageHeadingDescription>{t('onboardingProgress.complete.redirecting')}</PageHeadingDescription>
      </PageHeading>
      <Button size="lg" variant="primary" asChild>
        <a href={import.meta.env.VITE_APP_EXCHANGE_URL}>{t('buttons.continue')}</a>
      </Button>
    </BaseLayout>
  );
};
OnboardingCompletePageBasicContent.displayName = 'OnboardingCompletePageBasicContent';

const OnboardingCompletePageEnhancedContent = () => {
  const { t } = useTranslation();

  return (
    <BaseLayout className="space-y-8" id={ID} size="lg">
      <PageHeading className="mb-0 -md:text-left" centered>
        <PageHeadingTitle>{t('onboardingProgress.complete.nextSteps.title')}</PageHeadingTitle>
        <PageHeadingDescription className="whitespace-pre-line">{t('onboardingProgress.complete.nextSteps.subTitle')}</PageHeadingDescription>
      </PageHeading>
      <div className="flex flex-col md:flex-row md:items-stretch justify-center gap-4">
        <a className="flex-1" href={`${import.meta.env.VITE_APP_EXCHANGE_URL}/trade#Deposit`}>
          <LinkCard className="md:h-full">
            <LinkCardHeader>
              <GradientIcon className="shrink-0 size-10 md:size-32 md:mx-auto" strokeWidth={1.5}>
                <Banknote />
              </GradientIcon>
            </LinkCardHeader>
            <LinkCardContent>
              <LinkCardTitle asChild>
                <h2 className="md:-mx-4">{t('onboardingProgress.complete.nextSteps.deposit.title')}</h2>
              </LinkCardTitle>
              <LinkCardDescription>{t('onboardingProgress.complete.nextSteps.deposit.description')}</LinkCardDescription>
            </LinkCardContent>
          </LinkCard>
        </a>
        <Link className="flex-1" to="/futures" replace>
          <LinkCard className="md:h-full">
            <LinkCardHeader>
              <GradientIcon className="shrink-0 size-10 md:size-32 md:mx-auto" strokeWidth={1.5}>
                <TrendingUp />
              </GradientIcon>
            </LinkCardHeader>
            <LinkCardContent>
              <LinkCardTitle asChild>
                <h2 className="md:-mx-4">{t('onboardingProgress.complete.nextSteps.futuresSignup.title')}</h2>
              </LinkCardTitle>
              <LinkCardDescription>{t('onboardingProgress.complete.nextSteps.futuresSignup.description')}</LinkCardDescription>
            </LinkCardContent>
          </LinkCard>
        </Link>
      </div>
      <footer className="flex flex-col items-center gap-4">
        <Button size="lg" variant="link" asChild>
          <a href={import.meta.env.VITE_APP_EXCHANGE_URL}>{t('onboardingProgress.complete.nextSteps.continue')}</a>
        </Button>
      </footer>
    </BaseLayout>
  );
};

const OnboardingCompletePage = () => {
  const { setCurrentStep } = useProgress();
  const { data: verificationInformation, isLoading: isLoadingVerificationInformation } = useProfileVerificationInformation();
  const isCompleteAml = !!verificationInformation?.aml.date;
  const isVerificationStarted = !!verificationInformation?.verification && verificationInformation.verification.status !== 'NOT_VERIFIED';
  const { isWaiting: isWaitingForVerification } = useProfileVerificationSubmissionWait(isCompleteAml && isVerificationStarted);
  const { isIdle: isIdlePutUser, mutate: putUser } = useUserPutMutation();
  const isPendingPutUser = useIsMutating({ mutationKey: PUT_USER_QUERY_KEY }) > 0;
  const { futuresEnabled, futuresPublicTrading } = useFlags<LdFeatureFlags>();
  const { t } = useTranslation();
  const accountType = getAccountType();
  const hasTriggeredPutUser = useRef(false);
  const shouldPutUser = isCompleteAml && !isWaitingForVerification;
  const isPuttingUser = shouldPutUser && (isIdlePutUser || isPendingPutUser);
  const isLoading = isLoadingVerificationInformation || isWaitingForVerification || isPuttingUser;
  const isFuturesEnabledWithPublicTradingEnabled = futuresEnabled && futuresPublicTrading.enabled;

  useEffect(() => {
    return setCurrentStep(OnboardingProgressStep.PERSONAL_TRADING_INFORMATION, 1);
  }, [setCurrentStep]);

  useEffect(() => {
    if (shouldPutUser && !hasTriggeredPutUser.current) {
      hasTriggeredPutUser.current = true;

      putUser();
    }
  }, [putUser, shouldPutUser]);

  const { isRedirecting } = useOnboardingCompleteRedirect(isLoading);

  if (isLoading) return <LoadingPage />;

  if (isRedirecting)
    return (
      <LoadingPage>
        <p>{t('redirecting')}</p>
      </LoadingPage>
    );

  if (!isCompleteAml) return <Navigate to="/onboarding/aml-questionnaire" replace />;

  if (isFuturesEnabledWithPublicTradingEnabled) {
    if (accountType === AccountType.FUTURES) return <Navigate to="/futures" replace />;

    return <OnboardingCompletePageEnhancedContent />;
  }

  return <OnboardingCompletePageBasicContent />;
};
OnboardingCompletePage.displayName = 'OnboardingCompletePage';

export default OnboardingCompletePage;
